import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextareaAutosize,
  Box,
  FormControlLabel,
  Typography,
  Switch,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import HttpHelper from '../../utils/HttpHelper';
import { NavLink } from 'react-router-dom';
import { useJobContext } from '../../JobContext';
import useJobForm from '../../hooks/useJobForm';
import { current } from '@reduxjs/toolkit';
import SelectingAssessments from '../Assessments/SelectingAssessments';
import { useAssessmentManager } from '../../hooks/UseAssessmentManager';
import CustomizedModal from '../../components/common/CustomizedModal';
import QuillTextField from '../../components/common/QuillTextField';
import { normalizeQuill } from '../../utils/quill';

const EditJobDetails: React.FC = () => {
  const jobId = useParams().jobId;

  const {
    title,
    setJobTitle,
    description,
    setJobDescription,
    requirements,
    setJobRequirements,
    responsibilities,
    setJobResponsibilities,
    remote,
    setRemote,
    city,
    setCity,
    province,
    setProvince,
    country,
    setCountry,
    published,
    setPublished,
    currentlyAvailable,
    setCurrentlyAvailable,
    salary,
    setSalary,
    selectedAssessment,
    setSelectedAssessment,
    jobCategory,
    setJobCategory,
    jobType,
    setJobType,
    applicationDeadline,
    setApplicationDeadline,
    handleJobCategoryChange,
    handleJobTypeChange,
    handleCurrentlyAvailableChange,
    handleRemoteChange,
    handlePublishedChange,
  } = useJobForm();

  const { jobCategoryMapping } = useJobContext();
  const { jobTypeMapping } = useJobContext();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [showSecondaryButton, setShowSecondaryButton] = useState(true);

  const [initialPublished, setInitialPublished] = useState<boolean>(false);
  const [isSelectingAssessmentsEnabled, setIsSelectingAssessmentsEnabled] =
    useState(true);
  const [initialAssessments, setInitialAssessments] = useState<number[]>([]);

  const {
    assessmentsList,
    selectedAssessments,
    searchQuery,
    updateSelectedAssessments,
    handleSearch,
    handleSelectAssessment,
    handleSearchChange,
    handleDrop,
    handleDragStart,
    handleDragOver,
  } = useAssessmentManager();

  const jobCategoryMapInverse = Object.fromEntries(
    Object.entries(jobCategoryMapping).map(([key, value]) => [value, key]),
  );
  const jobTypeMapInverse = Object.fromEntries(
    Object.entries(jobTypeMapping).map(([key, value]) => [value, key]),
  );

  const formatDateToYYYYMMDD = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const navigate = useNavigate();

  useEffect(() => {
    // This effect fetches job details without assessments
    const fetchJobDetails = async () => {
      const jobResponse = await HttpHelper.get(`jobs/${jobId}`);
      if (jobResponse && jobResponse.job) {
        const { job } = jobResponse;
        setJobTitle(job.title);
        setJobDescription(job.description);
        setJobRequirements(job.requirements);
        setJobResponsibilities(job.responsibilities);
        setInitialPublished(job.published);
        setRemote(job.remote);
        setCity(job.city);
        setProvince(job.province);
        setCountry(job.country);
        setPublished(job.published);
        setSalary(job.salary);
        setApplicationDeadline(job.applicationDeadline);
        setCurrentlyAvailable(job.currentlyAvailable);
        setJobCategory(
          Object.keys(jobCategoryMapping).find(
            key => jobCategoryMapping[key] === job.jobCategory,
          ) || '',
        );
        setJobType(
          Object.keys(jobTypeMapping).find(
            key => jobTypeMapping[key] === job.jobType,
          ) || '',
        );
      }
    };

    fetchJobDetails();
  }, [jobId, jobCategoryMapping, jobTypeMapping]);

  useEffect(() => {
    const fetchAssessments = async () => {
      const assessmentsResponse = await HttpHelper.get(
        `jobs/${jobId}/assessments`,
      );
      if (assessmentsResponse && assessmentsResponse.assessments) {
        const assessmentIds = assessmentsResponse.assessments.map(
          (a: any) => a.id,
        );
        setInitialAssessments(assessmentIds);
        updateSelectedAssessments(assessmentsResponse.assessments);
      }
    };

    fetchAssessments();
  }, [jobId, modalOpen]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Prevent editing published jobs client-side, but allow toggling the publish switch
    if (initialPublished && published === initialPublished) {
      setModalTitle('Editing Disabled');
      setModalMessage(
        'Published jobs cannot be edited. Unpublish the job to make changes.',
      );
      setShowSecondaryButton(false);
      setModalOpen(true);
      return;
    }

    const currentAssessmentIds = selectedAssessments.map(a => a.id).sort((a, b) => a - b);
    const sortedInitialAssessments = [...initialAssessments].sort((a, b) => a - b);

    const hasChanges =
      currentAssessmentIds.length !== sortedInitialAssessments.length ||
      !currentAssessmentIds.every(
        (value, index) => value === sortedInitialAssessments[index],
      );

    const todayDate: Date = new Date();

    const updatedJob = {
      job: {
        title: title,
        description: normalizeQuill(description),
        requirements: normalizeQuill(requirements),
        responsibilities: normalizeQuill(responsibilities),
        remote: remote,
        city: city,
        province: province,
        country: country,
        published: published,
        salary: salary,
        datePosted: formatDateToYYYYMMDD(todayDate),
        jobType: jobTypeMapping[jobType],
        jobCategory: jobCategoryMapping[jobCategory],
        applicationDeadline: applicationDeadline,
        currentlyAvailable: currentlyAvailable,
      },
    };

    if (!hasChanges) {
      await HttpHelper.put(`jobs/${jobId}/update`, updatedJob);

      // Navigate directly without showing modal for simple job updates
      navigate('/manage-jobs');
      return;
    }

    await HttpHelper.put(`jobs/${jobId}/update`, updatedJob);
    const assessmentIds = selectedAssessments.map(
      (assessment: any) => assessment.id,
    );
    const data = { assessmentIds };

    try {
      await HttpHelper.put(`jobs/${jobId}/assessments/add`, data);
      setInitialAssessments(currentAssessmentIds);

      // Navigate directly on successful update
      navigate('/manage-jobs');
    } catch (error) {
      console.error('Update failed: ', error);

      // Show error message for assessments (job details were saved)
      setModalTitle('Partial Update');
      setModalMessage(
        'Job details were saved successfully. However, assessments cannot be modified because applicants have already applied or submitted assessments for this job.',
      );
      setShowSecondaryButton(true);
      setModalOpen(true);
      setIsSelectingAssessmentsEnabled(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ width: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: 2,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Edit Job
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={published}
              onChange={handlePublishedChange}
              name="published"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'var(--ttg-green)',
                  '&:hover': {
                    backgroundColor: 'var(--button-hover-green)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'var(--ttg-green)',
                },
              }}
            />
          }
          label="Published"
          labelPlacement="start"
          sx={{ marginLeft: 'auto' }}
        />
      </Box>
      <TextField
        label="Job Title"
        variant="outlined"
        value={title}
        onChange={e => setJobTitle(e.target.value)}
        fullWidth
        margin="normal"
      />
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        <FormControl fullWidth margin="normal">
          <InputLabel id="job-category-label">Job Category</InputLabel>
          <Select
            labelId="job-category-label"
            value={jobCategory}
            label="Job Category"
            onChange={handleJobCategoryChange}
          >
            {Object.keys(jobCategoryMapping).map(category => {
              return <MenuItem value={category}>{category}</MenuItem>;
            })}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel id="job-type-label">Job Type</InputLabel>
          <Select
            labelId="job-type-label"
            value={jobType}
            label="Job Type"
            onChange={handleJobTypeChange}
          >
            {Object.keys(jobTypeMapping).map(type => {
              return <MenuItem value={type}>{type}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        <TextField
          label="City"
          variant="outlined"
          value={city}
          onChange={e => setCity(e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Province"
          variant="outlined"
          value={province}
          onChange={e => setProvince(e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Country"
          variant="outlined"
          value={country}
          onChange={e => setCountry(e.target.value)}
          fullWidth
          margin="normal"
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        <TextField
          label="Application Deadline"
          variant="outlined"
          value={applicationDeadline}
          onChange={e => setApplicationDeadline(e.target.value)}
          type="date"
          InputLabelProps={{ shrink: true }}
          fullWidth
          margin="normal"
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        <TextField
          label="Salary"
          variant="outlined"
          type="number"
          value={salary}
          onChange={e => setSalary(e.target.value)}
          fullWidth
          margin="normal"
          sx={{ flexGrow: 1 }}
          InputProps={{ inputProps: { min: 0 } }}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="availability-label">Availability</InputLabel>
          <Select
            labelId="availability-label"
            value={
              currentlyAvailable ? 'currently-available' : 'future-opening'
            }
            label="Availability"
            onChange={handleCurrentlyAvailableChange}
            fullWidth
          >
            <MenuItem value="currently-available">Currently Available</MenuItem>
            <MenuItem value="future-opening">Future Opening</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={remote}
              onChange={handleRemoteChange}
              name="remote"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'var(--ttg-green)',
                  '&:hover': {
                    backgroundColor: 'var(--button-hover-green)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'var(--ttg-green)',
                },
              }}
            />
          }
          label="Remote"
          labelPlacement="start"
          sx={{ marginLeft: 'auto' }}
        />
      </Box>
      {/* <TextField
        label="Job Description"
        variant="outlined"
        value={description}
        onChange={e => setJobDescription(e.target.value)}
        multiline
        rows={4}
        fullWidth
        margin="normal"
      /> */}
      <QuillTextField
        label="Job Description"
        value={description}
        onChange={setJobDescription}
        minHeight={200}
      />
      {/* <TextField
        label="Responsibilities"
        variant="outlined"
        value={responsibilities}
        onChange={e => setJobResponsibilities(e.target.value)}
        multiline
        rows={4}
        fullWidth
        margin="normal"
      /> */}
      <QuillTextField
        label="Responsibilities"
        value={responsibilities}
        onChange={setJobResponsibilities}
        minHeight={200}
      />
      {/* <TextField
        label="Requirements"
        variant="outlined"
        value={requirements}
        onChange={e => setJobRequirements(e.target.value)}
        multiline
        rows={4}
        fullWidth
        margin="normal"
      /> */}
      <QuillTextField
        label="Requirements"
        value={requirements}
        onChange={setJobRequirements}
        minHeight={200}
      />
      <SelectingAssessments
        assessmentsList={assessmentsList}
        selectedAssessments={selectedAssessments}
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        handleSelectAssessment={handleSelectAssessment}
        handleSearchChange={handleSearchChange}
        handleDrop={handleDrop}
        handleDragStart={handleDragStart}
        handleDragOver={handleDragOver}
        handleSubmit={handleSubmit}
      />
      <Button
        type="submit"
        variant="contained"
        sx={{
          backgroundColor: 'var(--ttg-green)',
          '&:hover': {
            backgroundColor: 'var(--button-hover-green)',
          },
          marginTop: 3,
          marginBottom: 3,
        }}
      >
        Save Changes
      </Button>
      {modalOpen && (
        <CustomizedModal
          open={modalOpen}
          clickHandler={() => {
            setModalOpen(false);
          }}
          secondaryClickHandler={() => {
            setModalOpen(false);
            navigate('/manage-jobs');
          }}
          title={modalTitle}
          description={modalMessage}
          buttonText="Close"
          secondaryButtonText={showSecondaryButton ? "Go to job list" : undefined}
        />
      )}
    </Box>
  );
};

export default EditJobDetails;
